import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Camera, Plus, Filter, Grid, List, Search } from 'lucide-react';
import PhotoPost, { PhotoPostData } from '../components/media/PhotoPost';
import PhotoGallery from '../components/media/PhotoGallery';
import PhotoFeed from '../components/media/PhotoFeed';

interface Photo {
  id: string;
  url: string;
  title: string;
  description?: string;
  uploadDate: string;
  likes: number;
  comments: number;
  isLiked: boolean;
  tags: string[];
  category: 'training' | 'competition' | 'personal' | 'achievements';
}

interface PhotoFeedPost {
  id: string;
  user: {
    id: string;
    name: string;
    avatar: string;
    verified: boolean;
    sport?: string;
  };
  content: string;
  photos: string[];
  location?: string;
  tags: string[];
  category: string;
  likes: number;
  comments: number;
  isLiked: boolean;
  createdAt: string;
}

const Photos = () => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<'feed' | 'gallery' | 'upload'>('feed');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [feedPosts, setFeedPosts] = useState<PhotoFeedPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Mock data for demonstration
  const mockPhotos: Photo[] = [
    {
      id: '1',
      url: 'https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      title: 'Entrenamiento matutino',
      description: 'Sesión de entrenamiento en el gimnasio',
      uploadDate: '2024-03-15T08:00:00Z',
      likes: 45,
      comments: 8,
      isLiked: false,
      tags: ['entrenamiento', 'gimnasio', 'fuerza'],
      category: 'training'
    },
    {
      id: '2',
      url: 'https://images.pexels.com/photos/3621105/pexels-photo-3621105.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      title: 'Victoria en el campeonato',
      description: 'Celebrando la victoria con el equipo',
      uploadDate: '2024-03-10T18:30:00Z',
      likes: 128,
      comments: 24,
      isLiked: true,
      tags: ['victoria', 'campeonato', 'equipo'],
      category: 'achievements'
    },
    {
      id: '3',
      url: 'https://images.pexels.com/photos/3621106/pexels-photo-3621106.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      title: 'Preparación pre-juego',
      description: 'Rutina de calentamiento antes del partido',
      uploadDate: '2024-03-08T15:45:00Z',
      likes: 67,
      comments: 12,
      isLiked: false,
      tags: ['calentamiento', 'preparación', 'rutina'],
      category: 'training'
    }
  ];

  const mockFeedPosts: PhotoFeedPost[] = [
    {
      id: '1',
      user: {
        id: '1',
        name: 'María Rodriguez',
        avatar: 'https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        verified: true,
        sport: 'Basketball'
      },
      content: '¡Increíble sesión de entrenamiento hoy! El equipo está más fuerte que nunca. 💪🏀 #TeamWork #Training',
      photos: [
        'https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        'https://images.pexels.com/photos/3621105/pexels-photo-3621105.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        'https://images.pexels.com/photos/3621106/pexels-photo-3621106.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
      ],
      location: 'Centro Deportivo Nacional',
      tags: ['entrenamiento', 'equipo', 'basketball'],
      category: 'training',
      likes: 89,
      comments: 15,
      isLiked: false,
      createdAt: '2024-03-15T10:30:00Z'
    },
    {
      id: '2',
      user: {
        id: '2',
        name: 'Sofia Williams',
        avatar: 'https://images.pexels.com/photos/3764013/pexels-photo-3764013.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        verified: true,
        sport: 'Tennis'
      },
      content: 'Nuevo récord personal en el torneo de hoy! Gracias a todos por el apoyo 🎾✨',
      photos: [
        'https://images.pexels.com/photos/5384429/pexels-photo-5384429.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
      ],
      location: 'Club de Tenis Metropolitano',
      tags: ['tenis', 'record', 'torneo'],
      category: 'competition',
      likes: 156,
      comments: 28,
      isLiked: true,
      createdAt: '2024-03-14T16:20:00Z'
    }
  ];

  useEffect(() => {
    document.title = 'Fotos - FemSportNet';
    
    const fetchPhotos = async () => {
      try {
        setIsLoading(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setPhotos(mockPhotos);
        setFeedPosts(mockFeedPosts);
      } catch (error) {
        console.error('Error fetching photos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  const handlePhotoUpload = async (postData: PhotoPostData) => {
    setUploadLoading(true);
    try {
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real app, you would upload to Supabase Storage and create database records
      console.log('Uploading photos:', postData);
      
      // Create new feed post
      const newPost: PhotoFeedPost = {
        id: Math.random().toString(36).substring(2, 15),
        user: {
          id: user?.id || '',
          name: user?.name || '',
          avatar: user?.avatar_url || '',
          verified: user?.verified || false,
          sport: user?.sport || undefined
        },
        content: postData.content,
        photos: postData.photos.map(file => URL.createObjectURL(file)),
        location: postData.location,
        tags: postData.tags,
        category: postData.category,
        likes: 0,
        comments: 0,
        isLiked: false,
        createdAt: new Date().toISOString()
      };

      setFeedPosts(prev => [newPost, ...prev]);
      setActiveView('feed');
      
      alert('¡Fotos compartidas exitosamente!');
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Error al subir las fotos. Intenta nuevamente.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleLike = (postId: string) => {
    setFeedPosts(prev => prev.map(post => {
      if (post.id === postId) {
        const isLiked = !post.isLiked;
        return {
          ...post,
          isLiked,
          likes: isLiked ? post.likes + 1 : post.likes - 1
        };
      }
      return post;
    }));
  };

  const handleComment = (postId: string) => {
    console.log('Comment on post:', postId);
    // In a real app, this would open a comment modal or navigate to post detail
  };

  const handleShare = (postId: string) => {
    console.log('Share post:', postId);
    // In a real app, this would open a share modal
    if (navigator.share) {
      navigator.share({
        title: 'FemSportNet',
        text: 'Mira esta foto en FemSportNet',
        url: window.location.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href);
      alert('Enlace copiado al portapapeles');
    }
  };

  const handlePhotoLike = (photoId: string) => {
    setPhotos(prev => prev.map(photo => {
      if (photo.id === photoId) {
        const isLiked = !photo.isLiked;
        return {
          ...photo,
          isLiked,
          likes: isLiked ? photo.likes + 1 : photo.likes - 1
        };
      }
      return photo;
    }));
  };

  const handlePhotoComment = (photoId: string) => {
    console.log('Comment on photo:', photoId);
  };

  const handlePhotoShare = (photoId: string) => {
    console.log('Share photo:', photoId);
  };

  const handlePhotoDelete = (photoId: string) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-8 transition-colors duration-200">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Fotos
                </h1>
                <p className="text-gray-600 dark:text-gray-300">
                  Comparte y descubre fotos de la comunidad deportiva
                </p>
              </div>
              
              <button
                onClick={() => setActiveView('upload')}
                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
              >
                <Plus size={18} className="mr-2" />
                Subir Fotos
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center space-x-1 mb-8 bg-white dark:bg-gray-800 rounded-lg p-1 shadow">
            <button
              onClick={() => setActiveView('feed')}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                activeView === 'feed'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Camera size={18} className="mr-2" />
              Feed de Fotos
            </button>
            <button
              onClick={() => setActiveView('gallery')}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                activeView === 'gallery'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Grid size={18} className="mr-2" />
              Mi Galería
            </button>
            <button
              onClick={() => setActiveView('upload')}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                activeView === 'upload'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Plus size={18} className="mr-2" />
              Subir
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex justify-center my-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <>
              {activeView === 'feed' && (
                <PhotoFeed
                  posts={feedPosts}
                  onLike={handleLike}
                  onComment={handleComment}
                  onShare={handleShare}
                />
              )}

              {activeView === 'gallery' && (
                <PhotoGallery
                  photos={photos}
                  isOwner={true}
                  onLike={handlePhotoLike}
                  onComment={handlePhotoComment}
                  onShare={handlePhotoShare}
                  onDelete={handlePhotoDelete}
                />
              )}

              {activeView === 'upload' && (
                <PhotoPost
                  onSubmit={handlePhotoUpload}
                  onCancel={() => setActiveView('feed')}
                  isLoading={uploadLoading}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Photos;